import * as S from './Evaluation.styles'
import {
  GolferShortName,
  LockStatus,
  MITee,
  TeamRole,
} from '@app/evaluation-core/types'
import React, { useEffect, useRef, useState } from 'react'
import {
  getAdjustmentValue,
  resetHole,
  setHoleStatus,
} from '@app/evaluation-core'
import FeatherIcon from '@ovaeasy/react-native-vector-icons/Feather'

import { AdjustmentGroups } from '@app/evaluation-adjustments'
import Avatar from '@app/components/atoms/Avatar'
import UserAvatarPopover from '@app/components/atoms/Avatar/UserAvatarPopover'
import { DocumentHelmet } from '@app/helmet/Helmet'
import { EvaluationBoundary } from '../../boundary'
import EvaluationSideBar from '@app/components/molecules/EvaluationSideBar'
import FilterTees from '@app/components/molecules/FilterTees'
import LiveUpdatesModal from '@app/components/organisms/LiveUpdatesModal'
import ModalOverlay from '@app/evaluation-modal'
import { ActivityIndicator, View } from 'react-native'
import {
  getModification,
  Modifications,
  resetModifications,
  saveAdjustment,
} from '@app/evaluation-core/modifications'
import { setSelectedHole } from '@app/evaluation-core/actions/evaluationActions'
import theme from '@app/ui/theme'
import { useDispatch } from 'react-redux'
import { useEvaluation } from '@common/hooks'
import useEvaluationLockedState from '@common/hooks/useEvaluationLockedState'
import useEvaluationUser from '@common/hooks/useEvaluationUser'
import useFilterTees from '@common/hooks/useFilterTees'
import useModifications from '@common/hooks/useModifications'
import { getPlayerShots } from '@common/hooks/getPlayerShots'
import { useIsFocused } from '@react-navigation/native'
import { useMediaQuery } from 'react-responsive'
import useNotification from '@common/hooks/useNotification'
import useUser from '@common/hooks/useUser'
import { useRTDBConnection } from '@common/hooks/databaseConnection'
import Spacer from 'react-styled-spacer'
import InfoDropdown from '@app/components/molecules/InfoDropdown'
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import DateTimeView from '@app/components/atoms/DateTimeView'
import useAdjustment from '@common/hooks/useAdjustment'
import { useSnackbar } from '@app/snackbar'
import { EvalStatus } from '@app/courses/domain/ratingStatus'
import MobileEvaluationSideBar from '@app/components/molecules/EvaluationSideBar/MobileEvaluationSideBar'
import useReadOnly from '../hooks/useReadOnly'
import { CopiedTeeProvider } from './CopiedTeeContext'
import { EvaluationActionMenu } from './EvaluationActionMenu'
import { GolferNavigator } from '@app/evaluation-modal/components/GolferNavigator'
import { ConfirmGolferNavigateModal } from '@app/evaluation-modal/components/ConfirmGolferNavigateModal'
import { MobileTeeItem } from './MobileTeeItem'
import { ResetHoleConfirmationModal } from './ResetHoleConfirmationModal'
import { TeeItem } from './TeeItem'
import { TeeItemHeader } from './TeeItemHeader'

export interface EvaluationProps {}

const Evaluation: React.FC<EvaluationProps> = () => {
  const { isConnected, connect, loading } = useRTDBConnection()
  const isPhone = window.innerWidth <= 767
  const holeLocked = useEvaluationLockedState()
  const isTabletOrMobile = useMediaQuery({ query: '(max-width: 1180px)' })
  const [collapsedMode, setCollapsedMode] = useState(false)
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [prepSubmitted, setPredSubmitted] = useState(false)

  const [sideBarHeight, setSideBarHeight] = useState(0)

  const sideBarRef = useRef<HTMLDivElement>(null)
  const closeModal = () => setShowCancelModal(false)
  const openModal = () => setShowCancelModal(true)
  const dropMenuRef = useRef<HTMLDivElement>(
    null
  ) as React.MutableRefObject<HTMLDivElement>
  const isFocused = useIsFocused()
  const dispatch = useDispatch<any>()
  const userId = useUser()?.id
  const [shouldShowNotifications, holeNotifications, holeObj] =
    useNotification()

  const { currentId, evaluation } = useEvaluation()
  const { view, isAdmin, isTeamLeader } = useEvaluationUser()

  const [
    filteredTees,
    filterStatusValue,
    setFilterStatusValue,
    womenMenStatusValue,
    setWomenMenStatusValue,
  ] = useFilterTees()

  const modifications = useModifications()
  // getPlayerShots is a hook (useState/useEffect inside) despite its name
  const shots = getPlayerShots()
  const [pendingNavigation, setPendingNavigation] =
    useState<GolferShortName | null>(null)

  useEffect(() => {
    setSideBarHeight(sideBarRef.current?.clientHeight || 0)
  }, [sideBarRef.current?.clientHeight])

  useEffect(() => {
    setCollapsedMode(isTabletOrMobile)
  }, [isTabletOrMobile])

  if (!evaluation.holes) {
    return null
  } else {
    if (isFocused && !prepSubmitted) {
      setPredSubmitted(true)
    }
  }

  const flippedLockStatus =
    holeLocked === LockStatus.UNLOCKED ? LockStatus.LOCKED : LockStatus.UNLOCKED

  const setHoleLocked = () =>
    setHoleStatus(currentId as string, flippedLockStatus, view?.hole)

  const lockAllHoles = () => {
    const allHolesIsLocked =
      evaluation?.holes.length > 0 &&
      evaluation?.holes?.filter((x) => x.locked).length ==
        evaluation?.holes.length

    evaluation.holes.forEach((hole, index) => {
      setHoleStatus(
        currentId as string,
        !allHolesIsLocked ? LockStatus.LOCKED : LockStatus.UNLOCKED,
        index
      )
    })
  }

  if (!currentId) return null

  if (!evaluation || !userId || !evaluation) return null

  const resetHoleFn = () => {
    resetModifications({ evaluationId: currentId, hole: view?.hole })
    resetHole(view?.hole, currentId)
    closeModal()
  }

  const setModalState = (
    hole: number | undefined,
    tee: number | undefined,
    golfer: GolferShortName,
    adjustment: number | undefined
  ) => dispatch(setSelectedHole(hole as number, tee, golfer, adjustment))

  const holeIncrementState = () => {
    if (view?.hole + 1 < evaluation.holes?.length) {
      setModalState(view?.hole + 1, view.tee, view?.golfer, view?.adjustment)
    }
  }
  const holeDecrementState = () => {
    if (view?.hole > 0)
      setModalState(view?.hole - 1, view?.tee, view?.golfer, view?.adjustment)
  }

  // Golfer navigation (moved from evaluation-modal/index.tsx)
  const teeItem: MITee | undefined =
    evaluation.holes[view?.hole]?.tees[view?.tee]

  const currentAdjustment = evaluation.adjustments.find(
    (adj) => adj.adjustmentIndex === view?.adjustment
  )

  const adjustmentIsShots =
    (currentAdjustment?.name === 'Topography' && shots >= 3) ||
    currentAdjustment?.shots ||
    currentAdjustment?.landingZones

  const hasModifiedValues =
    !!teeItem &&
    !!currentAdjustment &&
    !!modifications &&
    getModification(
      modifications,
      currentAdjustment.adjustmentIndex,
      { tee: teeItem.teeIndex, golfer: view.golfer, shot: view.shot },
      !!adjustmentIsShots
    ) === Modifications.ADJUSTED

  const availableGolfers = teeItem?.golfers ?? []
  const currentGolferIndex = availableGolfers.indexOf(view?.golfer)
  const prevGolfer = availableGolfers[currentGolferIndex - 1]
  const nextGolfer = availableGolfers[currentGolferIndex + 1]

  const navigateToGolfer = (target: GolferShortName) =>
    setModalState(view?.hole, view?.tee, target, view?.adjustment)

  const handleSaveAndNavigate = (targetGolfer: GolferShortName) => {
    if (holeLocked !== LockStatus.UNLOCKED || !teeItem) {
      navigateToGolfer(targetGolfer)
      return
    }
    saveAdjustment({
      evaluationId: currentId,
      adjustmentIndex: currentAdjustment?.adjustmentIndex as number,
      hole: view.hole,
      tee: teeItem.teeIndex,
      golfer: view.golfer,
      shot: view.shot,
    })
    navigateToGolfer(targetGolfer)
  }

  const requestGolferNavigation = (target: GolferShortName | undefined) => {
    if (target === undefined) return
    if (
      hasModifiedValues &&
      holeLocked === LockStatus.UNLOCKED &&
      evaluation.status !== EvalStatus.FINALIZED &&
      view?.adjustment !== 12
    ) {
      setPendingNavigation(target)
    } else {
      navigateToGolfer(target)
    }
  }

  const handlePrevGolfer = () => requestGolferNavigation(prevGolfer)
  const handleNextGolfer = () => requestGolferNavigation(nextGolfer)

  const handleConfirmAndNavigate = () => {
    if (pendingNavigation) {
      handleSaveAndNavigate(pendingNavigation)
      setPendingNavigation(null)
    }
  }

  const handleNavigateWithoutConfirming = () => {
    if (pendingNavigation) {
      navigateToGolfer(pendingNavigation)
      setPendingNavigation(null)
    }
  }

  const renderAdjustmentView = () => {
    return (
      <ModalOverlay
        open={view && view?.adjustment !== -1}
        setModalState={setModalState}
        height={sideBarHeight ? sideBarHeight : '100%'}
      >
        <AdjustmentGroups
          selectedGroup={view?.adjustment ?? -1}
          evaluationState={{ evaluation, currentId }}
        />
      </ModalOverlay>
    )
  }

  const renderTeeGridView = () => {
    return (
      <CopiedTeeProvider>
        {filteredTees &&
          filteredTees.map((tee, index) => (
            <S.TeeGridItem key={index} zIndexValue={index}>
              <TeeItemHeader tee={tee} index={index} />
              <TeeItem tee={tee} index={index} />
            </S.TeeGridItem>
          ))}
        {!filteredTees &&
          Array.from({ length: 5 }, (_, index) => (
            <S.SkeletonContainer key={index} zIndexValue={index}>
              <Skeleton width={270} height={40} />
              <Skeleton width={270} height={40} />
              <Skeleton
                width={270}
                height={600}
                style={{ marginTop: 5, marginRight: 10 }}
              />
            </S.SkeletonContainer>
          ))}
      </CopiedTeeProvider>
    )
  }

  const mobileRenderTeeGridView = () => {
    return (
      <CopiedTeeProvider>
        {filteredTees &&
          filteredTees.map((tee, index) => (
            <S.MobileTeeRowContainer key={index}>
              {isPhone ? (
                <MobileEvaluationSideBar
                  collapsedMode={false}
                  dropMenuRef={dropMenuRef}
                  setCollapsedMode={setCollapsedMode}
                  holeDecrementState={holeDecrementState}
                  holeIncrementState={holeIncrementState}
                  openModal={openModal}
                  setHoleLocked={setHoleLocked}
                  lockAllHoles={lockAllHoles}
                />
              ) : (
                <EvaluationSideBar
                  collapsedMode={false}
                  dropMenuRef={dropMenuRef}
                  setCollapsedMode={setCollapsedMode}
                  holeDecrementState={holeDecrementState}
                  holeIncrementState={holeIncrementState}
                  openModal={openModal}
                  setHoleLocked={setHoleLocked}
                  lockAllHoles={lockAllHoles}
                />
              )}
              <S.MobileTeeItemContainer>
                <TeeItemHeader tee={tee} index={index} />
                <MobileTeeItem tee={tee} index={index} />
              </S.MobileTeeItemContainer>
            </S.MobileTeeRowContainer>
          ))}
        {!filteredTees &&
          Array.from({ length: 5 }, (_, index) => (
            <S.SkeletonContainer key={index} zIndexValue={index}>
              <Skeleton width={270} height={40} />
              <Skeleton width={270} height={40} />
              <Skeleton
                width={270}
                height={600}
                style={{ marginTop: 5, marginRight: 10 }}
              />
            </S.SkeletonContainer>
          ))}
      </CopiedTeeProvider>
    )
  }

  const holeIsLocked = holeLocked === LockStatus.LOCKED
  const readOnly = holeLocked === LockStatus.READ_ONLY
  const adjustments = useAdjustment()

  const crDate = getAdjustmentValue(
    'hole',
    view,
    'CRDate',
    adjustments
  ) as string

  const holeLockedText = `Hole: ${
    view?.hole + 1
  } has been locked by your team leader.`

  const readOnlyText = `This rating is archived and in read-only mode.`
  const convertUTCToLocal = (dateString: string, dateOnly?: boolean) => {
    const date = new Date(dateString + 'Z')
    if (dateOnly) {
      return date.toLocaleDateString()
    }
    const formattedDate = date.toLocaleString('sv-SE')
    return formattedDate
  }
  const allHolesIsLocked =
    evaluation?.holes.length > 0 &&
    evaluation?.holes?.filter((x) => x.locked).length ==
      evaluation?.holes.length
  const getCrDate = () => {
    return convertUTCToLocal(crDate)
  }
  const getLockAllText = () => {
    const collapsedText = allHolesIsLocked ? 'Unlock all' : 'Lock all'
    const expandedText = allHolesIsLocked ? 'Unlock all' : 'Lock all'

    return collapsedMode ? collapsedText : expandedText
  }
  const getLockHoleText = () => {
    const collapsedText = holeIsLocked ? 'Unlock' : 'Lock'
    const expandedText = holeIsLocked ? 'Unlock Hole' : 'Lock Hole'

    return collapsedMode ? collapsedText : expandedText
  }

  const renderLockButton = ({
    title,
    onPress,
    disabled,
    locked,
  }: {
    title: string
    onPress: () => void
    disabled: boolean
    locked: boolean
  }) => {
    const baseText = locked ? theme.colors.white : theme.colors.dark
    const disabledText = theme.colors.dark1

    return (
      <S.LockButton
        type="button"
        onClick={onPress}
        disabled={disabled}
        locked={locked}
      >
        <FeatherIcon
          name={locked ? 'lock' : 'unlock'}
          size={isPhone ? 12 : 14}
          color={disabled ? disabledText : baseText}
        />
        <span>{title}</span>
      </S.LockButton>
    )
  }

  const [openSnackbar] = useSnackbar()
  const getTeamLeaderEmail = () => {
    const teamleader = Object.values(evaluation.users).find(
      (user) => user.role === TeamRole.LEADER
    )
    return teamleader?.view?.email
  }
  const handleLockAllButtonClick = () => {
    if (isAdmin) {
      lockAllHoles()
    } else {
      const teamleader = getTeamLeaderEmail()
      if (teamleader) {
        openSnackbar(
          `Only the Team Leader (${teamleader}) can lock/unlock holes`
        )
      } else {
        openSnackbar('Only the Team Leader can lock/unlock holes')
      }
    }
  }

  const handleReadOnly = useReadOnly()

  const handleLockButtonClick = () => {
    if (isAdmin) {
      handleReadOnly(setHoleLocked)()
    } else {
      const teamleader = getTeamLeaderEmail()
      if (teamleader) {
        openSnackbar(
          `Only the Team Leader (${teamleader}) can lock/unlock holes`
        )
      } else {
        openSnackbar('Only the Team Leader can lock/unlock holes')
      }
    }
  }

  const branchRevision = getAdjustmentValue(
    'hole',
    view,
    'BranchRevision',
    adjustments
  )

  const lockStatus = useEvaluationLockedState()
  const crVersion = getAdjustmentValue('hole', view, 'CRVersion', adjustments)
  const mobileEvaluationCard = (
    <S.MobileEvaluationCard
      modalShowing={shouldShowNotifications}
      inAdjustment={view?.adjustment !== -1}
    >
      {view?.adjustment !== -1 ? (
        <>
          {
            <S.MobileAdjustmentContainer>
              {renderAdjustmentView()}
            </S.MobileAdjustmentContainer>
          }
        </>
      ) : (
        <S.MobileTeeGridContainer>
          {mobileRenderTeeGridView()}
          <S.LiveCRInfoContainer>
            <S.MobileLockButtonContainer marginValue={!isPhone ? 4 : 2}>
              {renderLockButton({
                title: getLockHoleText(),
                onPress: handleLockButtonClick,
                disabled: lockStatus === LockStatus.READ_ONLY,
                locked: holeIsLocked,
              })}
            </S.MobileLockButtonContainer>
            <S.MobileLockButtonContainer marginValue={!isPhone ? 4 : 2}>
              {renderLockButton({
                title: getLockAllText(),
                onPress: handleLockAllButtonClick,
                disabled: evaluation.status == EvalStatus.FINALIZED,
                locked: allHolesIsLocked,
              })}
            </S.MobileLockButtonContainer>
            <S.LiveCRInfoTextContainer>
              {branchRevision === 0 ? (
                <Skeleton height={!isPhone ? 20 : 10} width={'90%'} />
              ) : (
                <S.LiveCRInfoText>
                  {collapsedMode ? `Rev: ` : `Revision: `}
                  <S.MobileRevisionText isPhone={isPhone}>
                    {branchRevision}
                  </S.MobileRevisionText>
                </S.LiveCRInfoText>
              )}
              {crVersion === 0 ? (
                <Skeleton height={!isPhone ? 20 : 10} width={'90%'} />
              ) : (
                <S.LiveCRInfoText>
                  {collapsedMode ? `CR v. ` : `LiveCR Version: `}
                  <S.MobileCRVersionText isPhone={isPhone}>
                    {crVersion}
                  </S.MobileCRVersionText>
                </S.LiveCRInfoText>
              )}
              <S.LiveCRInfoText>
                {!collapsedMode && 'Last synced: '}
                <DateTimeView
                  dateString={getCrDate()}
                  showTime={!collapsedMode}
                />
              </S.LiveCRInfoText>
            </S.LiveCRInfoTextContainer>
          </S.LiveCRInfoContainer>
        </S.MobileTeeGridContainer>
      )}
    </S.MobileEvaluationCard>
  )

  const TabBar = isPhone ? S.MobileTopBarContainer : S.TopBarContainer
  const ContentWrapperAdjusted = isPhone
    ? S.MobileContentWrapper
    : S.ContentWrapper

  return (
    <EvaluationBoundary>
      <S.Wrapper>
        <ContentWrapperAdjusted>
          <TabBar>
            <EvaluationActionMenu userId={String(userId)} />
            <Spacer w={isPhone ? 4 : 8} />
            <InfoDropdown evaluation={evaluation} relative={true} yAdj={40} />
            {view?.adjustment === -1 && <Spacer w={8} />}
            {view?.adjustment === -1 && (
              <FilterTees
                evaluation={evaluation}
                view={view}
                filterStatusValue={filterStatusValue}
                setFilterStatusValue={setFilterStatusValue}
                setWomenMenStatusValue={setWomenMenStatusValue}
                womenMenStatusValue={womenMenStatusValue}
              />
            )}
            <Spacer w={8} />
            {isTeamLeader && (
              <S.LockButtonsContainer>
                {renderLockButton({
                  title: getLockHoleText(),
                  onPress: handleLockButtonClick,
                  disabled: lockStatus === LockStatus.READ_ONLY,
                  locked: holeIsLocked,
                })}
                {renderLockButton({
                  title: getLockAllText(),
                  onPress: handleLockAllButtonClick,
                  disabled: evaluation.status == EvalStatus.FINALIZED,
                  locked: allHolesIsLocked,
                })}
              </S.LockButtonsContainer>
            )}
            <Spacer w={16} />
            <GolferNavigator
              onPrev={handlePrevGolfer}
              onNext={handleNextGolfer}
            />

            <S.HoleIsLockedContainer>
              <S.HoleLockedText>
                {holeIsLocked && holeLockedText}
                {readOnly && readOnlyText}
              </S.HoleLockedText>
            </S.HoleIsLockedContainer>

            {/* {view?.adjustment === -1 && (
              <LegendContainer>
                <LegendItem>
                  <LegendColor color={theme.colors.saved} />
                  <LegendText>Confirmed</LegendText>
                </LegendItem>
                <LegendItem>
                  <LegendColor color={theme.colors.yellow} />
                  <LegendText>Adjusted</LegendText>
                </LegendItem>
              </LegendContainer>
            )} */}
            <S.AvatarWrapper>
              <S.AvatarContainer
                padding={8}
                width={Object.values(evaluation.users)?.length * 30}
              >
                {Object.values(evaluation.users).map((user, i) => (
                  <UserAvatarPopover
                    key={user?.userId}
                    user={user}
                    isLastIndex={
                      i + 1 === Object.values(evaluation.users)?.length
                    }
                    zIndex={i}
                  />
                ))}
              </S.AvatarContainer>
            </S.AvatarWrapper>
          </TabBar>

          <DocumentHelmet title="Evaluation" description="" />
          {shouldShowNotifications && (
            <LiveUpdatesModal
              collapsedMode={collapsedMode}
              holeArray={holeNotifications}
              title={`LiveCR Updates - Hole: ${view?.hole + 1}`}
              description="Please accept or reject these all to proceed."
              isVisible={shouldShowNotifications}
              holeObj={holeObj}
              setLoading={console.log}
            />
          )}
          {isPhone ? (
            mobileEvaluationCard
          ) : (
            <S.EvaluationCard
              modalShowing={shouldShowNotifications}
              inAdjustment={view?.adjustment !== -1}
            >
              {view?.adjustment !== -1 ? (
                <S.AdjustmentViewContainer>
                  <S.AdjustmentSidebarContainer>
                    <EvaluationSideBar
                      collapsedMode={collapsedMode}
                      dropMenuRef={dropMenuRef}
                      setCollapsedMode={setCollapsedMode}
                      holeDecrementState={holeDecrementState}
                      holeIncrementState={holeIncrementState}
                      openModal={openModal}
                      setHoleLocked={setHoleLocked}
                      lockAllHoles={lockAllHoles}
                    />
                  </S.AdjustmentSidebarContainer>
                  <S.AdjustmentContentContainer>
                    {renderAdjustmentView()}
                  </S.AdjustmentContentContainer>
                </S.AdjustmentViewContainer>
              ) : (
                <>
                  <EvaluationSideBar
                    collapsedMode={collapsedMode}
                    dropMenuRef={dropMenuRef}
                    setCollapsedMode={setCollapsedMode}
                    holeDecrementState={holeDecrementState}
                    holeIncrementState={holeIncrementState}
                    openModal={openModal}
                    setHoleLocked={setHoleLocked}
                    lockAllHoles={lockAllHoles}
                  />
                  <S.TeeGridContainer>{renderTeeGridView()}</S.TeeGridContainer>
                </>
              )}
            </S.EvaluationCard>
          )}
          <ResetHoleConfirmationModal
            hole={view?.hole + 1}
            closeModal={closeModal}
            showModal={showCancelModal}
            acceptFunction={resetHoleFn}
          />
          <ConfirmGolferNavigateModal
            isVisible={!!pendingNavigation}
            onConfirmAndNavigate={handleConfirmAndNavigate}
            onNavigateWithoutConfirming={handleNavigateWithoutConfirming}
            onCancel={() => setPendingNavigation(null)}
          />
        </ContentWrapperAdjusted>
        {!isConnected && (
          <S.ConnectionButton onClick={connect}>
            {loading ? (
              <ActivityIndicator size={40} color={theme.colors.white} />
            ) : (
              <FeatherIcon
                name="cloud-off"
                size={40}
                color={theme.colors.white}
              />
            )}
            <S.ConnectionText>
              You have lost connection to the server.{'\n'}Please press this
              window to reconnect.
            </S.ConnectionText>
          </S.ConnectionButton>
        )}{' '}
      </S.Wrapper>
    </EvaluationBoundary>
  )
}

export default Evaluation
